/**
 * Step 1's item switches (round 6 addendum §3.5). The UI, Story Manager and the split check agree:
 *
 * - A personalized tile is on unless it is in `content.excluded_item_ids`.
 * - A static item (a note, link or still: `can_opt_in`) is off unless it is in
 *   `send_unfiltered_item_ids`, which sends it the same to everyone.
 * - Anything left out for another reason (masked, refused, a tool's view, can't be filtered) has no
 *   switch and shows its reason.
 * - A turned-off item is left out with reason `turned_off`.
 *
 * Pure; shared by step 1, the previews and the mock API. Tested in distributionItemSwitches.test.ts.
 */
import type { DistributionContentApi, LeftOutTileApi, SplitCheckApi, TileRefApi } from "./distributions.types";

/** Which switch an item carries: on unless turned off, off unless sent as is, or none. */
export type StoryItemSwitchApi = "personalized" | "as_is" | null;

export interface StoryItemState {
  tile: TileRefApi;
  switch: StoryItemSwitchApi;
  /** In every copy: a personalized tile left on, or a static item sent as is. */
  on: boolean;
  /** Why the split check leaves it out (static items too); null for a personalized tile. */
  leftOut: LeftOutTileApi | null;
}

/** The ids the owner turned off; content saved before round 6 has none. */
export const excludedItemIdsOf = (
  content: Pick<DistributionContentApi, "excluded_item_ids"> | null | undefined,
): string[] => content?.excluded_item_ids ?? [];

const refOf = ({ item_id, title, kind, sensitive }: TileRefApi): TileRefApi => ({ item_id, title, kind, sensitive });

/**
 * Every item of the split check with its switch, in the check's order (personalized first). A
 * left-out tile the server already reports as `turned_off` keeps its switch, so it can go back on.
 */
export const storyItemStates = (
  check: SplitCheckApi,
  excludedItemIds: readonly string[],
  sendUnfilteredIds: readonly string[],
): StoryItemState[] => [
  ...check.personalized.map((tile) => ({
    tile: refOf(tile),
    switch: "personalized" as const,
    on: !excludedItemIds.includes(tile.item_id),
    leftOut: null,
  })),
  ...check.left_out.map((tile): StoryItemState => {
    if (tile.reason === "turned_off") return { tile: refOf(tile), switch: "personalized", on: false, leftOut: null };
    if (tile.can_opt_in) return { tile: refOf(tile), switch: "as_is", on: sendUnfilteredIds.includes(tile.item_id), leftOut: tile };
    return { tile: refOf(tile), switch: null, on: false, leftOut: tile };
  }),
];

/**
 * The split check as a run applies it: each turned-off personalized tile moves to `left_out` with
 * reason `turned_off` and `message`. Unknown ids are ignored, as Story Manager ignores them.
 */
export const withTurnedOff = (
  check: SplitCheckApi,
  excludedItemIds: readonly string[],
  message: string,
): SplitCheckApi => {
  const off = check.personalized.filter((tile) => excludedItemIds.includes(tile.item_id));
  if (!off.length) return check;
  return {
    ...check,
    personalized: check.personalized.filter((tile) => !excludedItemIds.includes(tile.item_id)),
    left_out: [
      ...off.map((tile): LeftOutTileApi => ({ ...refOf(tile), reason: "turned_off", message, can_opt_in: false })),
      ...check.left_out,
    ],
  };
};

/** Lists or unlists one id, keeping the others in the order they were chosen. */
export const toggleItemId = (ids: readonly string[], itemId: string, listed: boolean): string[] =>
  listed ? (ids.includes(itemId) ? [...ids] : [...ids, itemId]) : ids.filter((id) => id !== itemId);

/**
 * The lists after one switch moves: a personalized tile is listed in `excluded` when it goes off,
 * a static item in `sendAsIs` when it goes on. An item with no switch changes nothing.
 */
export const applyItemSwitch = (
  lists: { excluded: readonly string[]; sendAsIs: readonly string[] },
  state: Pick<StoryItemState, "switch"> & { tile: Pick<TileRefApi, "item_id"> },
  on: boolean,
): { excluded: string[]; sendAsIs: string[] } => ({
  excluded: state.switch === "personalized" ? toggleItemId(lists.excluded, state.tile.item_id, !on) : [...lists.excluded],
  sendAsIs: state.switch === "as_is" ? toggleItemId(lists.sendAsIs, state.tile.item_id, on) : [...lists.sendAsIs],
});
